import { type Dispatch, type SetStateAction } from 'react';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { deleteCookie, setCookie } from 'cookies-next/client';
import { sha256 } from 'js-sha256';
import { useSearchParams } from 'next/navigation';

import { useConsentManager } from '../consent/hooks/useConsentManager';
import { useExtendedRouter } from '../routing/useExtendedRouter';

import { type AppleLoginErrorResponse, type LoginResponse } from './aliases';
import {
  type AccountLinkingCredential,
  type CombinedLoginErrorResponse,
  type LoginType,
} from './types';

import {
  ACCESS_TOKEN_KEY,
  ACCESS_TOKEN_OPTS,
  EXTERNAL_ID_KEY,
  HASHED_EXTERNAL_ID_KEY,
  HASHED_USER_EMAIL_KEY,
  USER_ID_KEY,
} from '@/constants/cookies';
import { RQ_USER_DATA_KEY } from '@/modules/ReactQuery/cacheKeys';
import { identify } from '@/modules/auth/api';
import {
  COOKIE_ACCOUNT_LINKING_MFA,
  COOKIE_ACCOUNT_LINKING_TOKEN_OPTIONS,
  COOKIE_MFA_TOKEN,
  COOKIE_MFA_TOKEN_OPTS,
  GENERIC_ERROR,
} from '@/modules/auth/constants';
import { syncGuestBag } from '@/modules/bag/helpers';
import {
  type AxiosCompatibleError,
  type AxiosCompatibleResponse,
} from '@/modules/http/types';
import { login, loginWithApple } from '@/modules/login/api';
import {
  deleteAccountLinkingCookies,
  handleAccountLinking,
  validateRedirect,
} from '@/modules/login/helpers';
import { recordCheckoutRedirectMetric } from '@/modules/login/recordCheckoutRedirectMetric';
import { useLoginTracking } from '@/modules/login/useLoginTracking';
import { navigateToUrl } from '@/modules/routing/helpers';
import { COOKIE_ZENDESK_SSO_STATE } from '@/modules/zendesk/constants.ts';
import {
  getZendeskRedirectUrl,
  getZendeskSSOStateValue,
} from '@/modules/zendesk/helpers';
import { setGTMCookies } from '@/utils/setExternalIdCookie';

type Props = {
  setError?: Dispatch<SetStateAction<string>>;
  loginType: LoginType;
  accountLinkingCredential?: AccountLinkingCredential | null;
  storedSSOEmail?: string;
};

export function useLoginMutations({
  setError,
  loginType,
  accountLinkingCredential,
  storedSSOEmail,
}: Props) {
  const {
    sendLoginEvents,
    sendLoginFailureEvents,
    sendLinkAccountSuccessEvent,
  } = useLoginTracking();
  const queryClient = useQueryClient();
  const redirect = useSearchParams().get('redirect') || '';
  const { resetAirgapConsent } = useConsentManager();

  const router = useExtendedRouter();

  async function handleLoginSuccess(token: string) {
    resetAirgapConsent();
    setCookie(ACCESS_TOKEN_KEY, token, ACCESS_TOKEN_OPTS);

    deleteCookie(COOKIE_MFA_TOKEN, COOKIE_MFA_TOKEN_OPTS);

    const { data: user } = await queryClient.fetchQuery({
      queryKey: [RQ_USER_DATA_KEY],
      queryFn: identify,
    });

    const userId = user.id.toString();
    const hashedUserId = sha256(userId);
    const hashedUserEmail = sha256(user.email);

    setGTMCookies({
      cookieRecord: {
        [EXTERNAL_ID_KEY]: userId,
        [HASHED_EXTERNAL_ID_KEY]: hashedUserId,
        [USER_ID_KEY]: userId,
        [HASHED_USER_EMAIL_KEY]: hashedUserEmail,
      },
    });

    const emailsMatch = storedSSOEmail === user.email;

    await Promise.allSettled([
      ...(emailsMatch
        ? [
            handleAccountLinking(
              accountLinkingCredential,
              sendLinkAccountSuccessEvent
            ),
          ]
        : []),
      syncGuestBag(user.id),
      sendLoginEvents(user, loginType),
    ]);

    if (accountLinkingCredential) {
      deleteAccountLinkingCookies();
    }

    // Check for Zendesk SSO state
    const returnTo = getZendeskSSOStateValue();
    let validatedRedirect;
    if (returnTo) {
      deleteCookie(COOKIE_ZENDESK_SSO_STATE);
      validatedRedirect = getZendeskRedirectUrl(returnTo);
    } else {
      validatedRedirect = validateRedirect(redirect, user);
      recordCheckoutRedirectMetric({
        intendedRedirect: redirect,
        validatedRedirect,
        flow: 'password_login',
      });
    }

    try {
      navigateToUrl(validatedRedirect);
    } catch {
      navigateToUrl('/');
    }
  }

  async function redirectToMFA(mfaToken: string) {
    setCookie(COOKIE_MFA_TOKEN, mfaToken, COOKIE_MFA_TOKEN_OPTS);

    // @PB TODO ADD WHEN ACCOUNT LINKING IS READY
    if (accountLinkingCredential) {
      setCookie(
        COOKIE_ACCOUNT_LINKING_MFA,
        JSON.stringify(accountLinkingCredential),
        COOKIE_ACCOUNT_LINKING_TOKEN_OPTIONS
      );
    }

    router.push(
      `/login/mfa${redirect ? `?redirect=${encodeURIComponent(redirect)}` : ''}`
    );
  }

  function onSuccess(response: AxiosCompatibleResponse<LoginResponse>) {
    return handleLoginSuccess(response.data.access_token);
  }

  async function onError(
    error: AxiosCompatibleError<CombinedLoginErrorResponse>
  ) {
    const maybeErrorBody = error?.response?.data;

    if (
      maybeErrorBody &&
      'payload' in maybeErrorBody &&
      error?.response?.status === 403 &&
      maybeErrorBody?.code === 'MFA_REQUIRED'
    ) {
      return redirectToMFA(maybeErrorBody?.payload.mfa_token);
    }

    sendLoginFailureEvents(loginType);

    if (!setError) {
      return;
    }

    const message = maybeErrorBody?.message;

    return setError(message || GENERIC_ERROR);
  }

  const appleLoginMutation = useMutation<
    AxiosCompatibleResponse<LoginResponse>,
    AxiosCompatibleError<AppleLoginErrorResponse>,
    { apple_user_id: string; apple_access_token: string }
  >({
    mutationFn: loginWithApple,
    onSuccess,
    onError: () => {
      sendLoginFailureEvents(loginType);
    },
  });

  const loginMutation = useMutation({
    mutationFn: login,
    onSuccess,
    onError,
  });

  return { appleLoginMutation, loginMutation };
}
